import React from "react";

/* 🔹 Helper to format address */
const formatAddress = (address) => {
  if (!address) return "N/A";
  const { line1, line2, city, postcode } = address;
  return [line1, line2, city, postcode].filter(Boolean).join(", ");
};

export default function CreditCardView({ data }) {
  if (!data) return null;

  return (
    <>
      <br />

      {/* Header */}
      <div className="w-full text-left p-3 flex items-center gap-3 bg-[#5a6db3] ">
        <h2 className="text-lg font-semibold">
          Credit Card Statement Details
        </h2>
      </div>

      {/* Info Sections */}
      <div className="border border-t-0 rounded-b-lg p-4 grid grid-cols-2 gap-6 bg-white shadow">

        {/* Cardholder Info */}
        <div>
          <h3 className="font-semibold mb-2">Cardholder Information</h3>
          <p><b>Name:</b> {data.cardholder_info?.name || "N/A"}</p>
          <p><b>Address:</b> {formatAddress(data.cardholder_info?.address)}</p>
        </div>

        {/* Card Issuer */}
        <div>
          <h3 className="font-semibold mb-2">Card Issuer</h3>
          <p><b>Bank Name:</b> {data.card_issuer?.name || "N/A"}</p>
          <p><b>Contact:</b> {data.card_issuer?.contact || "N/A"}</p>
          <p><b>Address:</b> {formatAddress(data.card_issuer?.address)}</p>
        </div>

        {/* Statement Info */}
        <div>
          <h3 className="font-semibold mb-2">Statement Information</h3>
          <p>
            <b>Account Number:</b>{" "}
            {data.statement_info?.accountNumber || "N/A"}
          </p>
          <p>
            <b>Statement Period:</b>{" "}
            {data.statement_info?.statementPeriod
              ? `${data.statement_info.statementPeriod.startDate} to ${data.statement_info.statementPeriod.endDate}`
              : "N/A"}
          </p>
          <p><b>Statement Date:</b> {data.statement_info?.statementDate || "N/A"}</p>
          <p><b>Due Date:</b> {data.statement_info?.dueDate || "N/A"}</p>
        </div>

        {/* Account Summary */}
        <div>
          <h3 className="font-semibold mb-2">Account Summary</h3>
          <p><b>Available Credit:</b> {data.account_summary?.availableCredit || "N/A"}</p>
          <p><b>Cash Advance Limit:</b> {data.account_summary?.cashAdvanceLimit || "N/A"}</p>
          <p><b>Credit Limit:</b> {data.account_summary?.creditLimit || "N/A"}</p>
          <p><b>Currency:</b> {data.account_summary?.currency || "N/A"}</p>
          <p><b>Fees Charges:</b> {data.account_summary?.feesCharges || "N/A"}</p>
          <p><b>Interest Charges:</b> {data.account_summary?.interestCharges || "N/A"}</p>
          <p><b>Minimum Payment Due:</b> {data.account_summary?.minimumPaymentDue || "N/A"}</p>
          <p><b>New Balance:</b> {data.account_summary?.newBalance || "N/A"}</p>
          <p><b>Payments Credits:</b> {data.account_summary?.paymentsCredits || "N/A"}</p>
          <p><b>Previous Balance:</b> {data.account_summary?.previousBalance || "N/A"}</p>
          <p><b>Purchases Debits:</b> {data.account_summary?.purchasesDebits || "N/A"}</p>
        </div>
      </div>

      {/* Payment Info */}
      <div
        className="mt-4 p-3 border rounded-lg text-black"
        style={{ background: "aliceblue" }}
      >
        <h3 className="font-semibold mb-2">Payment Information</h3>
        <p><b>Due Date:</b> {data.payment_info?.due_date || "N/A"}</p>
        <p><b>Minimum Payment:</b> {data.payment_info?.minimum_payment || "N/A"}</p>
        <p><b>Total Due:</b> {data.payment_info?.total_due || "N/A"}</p>
      </div>

      {/* Transactions */}
      <div className="border bg-white shadow mt-6">
        <h3 className="w-full text-left p-3 bg-[#5a6db3] ">
          Transactions
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full border rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 border">Date</th>
                <th className="px-4 py-2 border">Description</th>
                <th className="px-4 py-2 border text-right">Amount</th>
                <th className="px-4 py-2 border text-right">Type</th>
                <th className="px-4 py-2 border text-right">Reference No.</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.transactions) && data.transactions.length ? (
                data.transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{txn.date}</td>
                    <td className="px-4 py-2 border">{txn.description}</td>
                    <td
                      className={`px-4 py-2 text-right border ${
                        txn.amount < 0 ? "text-red-600" : "text-green-600"
                      }`}
                    >
                      {txn.amount}
                    </td>
                    <td className="px-4 py-2 text-right border">
                      {txn.transaction_type}
                    </td>
                    <td className="px-4 py-2 text-right border">
                      {txn.reference_number}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-3 border">
                    No transactions available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fees & Charges */}
      {Array.isArray(data.fees_and_charges) && data.fees_and_charges.length > 0 && (
        <div className="border bg-white shadow mt-6">
          <h3 className="w-full text-left p-3 bg-[#5a6db3] ">
            Fees & Charges
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full border rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 border">Fee Type</th>
                  <th className="px-4 py-2 border text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.fees_and_charges.map((fee, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{fee.type}</td>
                    <td className="px-4 py-2 text-right border">
                      {fee.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
